import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  TrendingUp, ShoppingBag, Clock, CheckCircle, Loader2, Phone, MapPin,
  RefreshCw, X, MessageCircle, Bot, User, Send, Hand, Bell, ExternalLink, Info
} from 'lucide-react';
import { API_URL } from '../../config';

// ============================================================
// Panneaux intégrés au module "Page Facebook" :
//  - SalesPanel        : ventes détectées + modal détail
//  - ConversationsPanel: inbox, réponse manuelle, prise de main
//  - TelegramPanel     : alertes Telegram en 1 clic
// ============================================================

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json'
});

const STATUS_LABELS = {
  pending:   { label: 'En attente', color: 'bg-yellow-500/20 text-yellow-300' },
  confirmed: { label: 'Confirmée',  color: 'bg-blue-500/20 text-blue-300' },
  completed: { label: 'Livrée',     color: 'bg-green-500/20 text-green-300' },
  cancelled: { label: 'Annulée',    color: 'bg-red-500/20 text-red-300' },
};

// ════════════════════════════════════════════════════════════
// 💰 PANNEAU VENTES
// ════════════════════════════════════════════════════════════
export function SalesPanel({ clientId, onOpenConversation }) {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bot/leads/${clientId}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) { setStats(data.stats); setLeads(data.leads); }
    } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  const updateStatus = async (leadId, status) => {
    await fetch(`${API_URL}/api/bot/leads/${clientId}/${leadId}/status`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ status })
    });
    setSelectedLead(null);
    loadLeads();
  };

  if (loading) return <PanelLoader />;

  return (
    <div className="space-y-4">
      {/* Explication */}
      <div className="flex items-start gap-2 p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-200 text-xs">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>
          Une « vente » n'est <strong>pas</strong> chaque message : votre agent IA n'enregistre une vente que lorsqu'un
          client <strong>confirme un achat</strong> (commande + coordonnées, paiement annoncé...). Cliquez sur une vente pour voir les détails.
        </span>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat icon={ShoppingBag} label="Total" value={stats.total} color="text-blue-300" />
          <MiniStat icon={TrendingUp} label="Ce mois" value={stats.this_month} color="text-purple-300" />
          <MiniStat icon={Clock} label="En attente" value={stats.pending} color="text-yellow-300" />
          <MiniStat icon={CheckCircle} label="Confirmées" value={stats.confirmed} color="text-green-300" />
        </div>
      )}

      {leads.length === 0 ? (
        <div className="p-6 text-center text-gray-400 text-sm">
          Aucune vente détectée pour le moment. Dès qu'un client confirme un achat dans Messenger, elle apparaîtra ici.
        </div>
      ) : (
        <div className="space-y-2">
          {leads.map(lead => {
            const st = STATUS_LABELS[lead.status] || STATUS_LABELS.pending;
            return (
              <button
                key={lead.id}
                onClick={() => setSelectedLead(lead)}
                className="w-full text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-3 transition-all"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-medium text-sm">{lead.sender_name || 'Client'}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${st.color}`}>{st.label}</span>
                  {lead.product && <span className="text-blue-200 text-xs">🛍️ {lead.product}</span>}
                  <span className="text-gray-500 text-xs ml-auto">
                    {lead.created_at ? new Date(lead.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Modal détail vente */}
      {selectedLead && (
        <Modal onClose={() => setSelectedLead(null)} title="Détail de la vente">
          <div className="space-y-3 text-sm">
            <DetailRow label="Client" value={selectedLead.sender_name} />
            <DetailRow label="Produit" value={selectedLead.product || '—'} />
            {selectedLead.quantity && <DetailRow label="Quantité" value={selectedLead.quantity} />}
            {selectedLead.phone && (
              <DetailRow label="Téléphone" value={<span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{selectedLead.phone}</span>} />
            )}
            {selectedLead.address && (
              <DetailRow label="Adresse" value={<span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{selectedLead.address}</span>} />
            )}
            <DetailRow label="Statut" value={
              <span className={`px-2 py-0.5 rounded-full text-xs ${(STATUS_LABELS[selectedLead.status] || STATUS_LABELS.pending).color}`}>
                {(STATUS_LABELS[selectedLead.status] || STATUS_LABELS.pending).label}
              </span>
            } />
            {selectedLead.original_message && (
              <div>
                <div className="text-gray-400 text-xs mb-1">Message du client qui a déclenché la vente :</div>
                <div className="bg-white/5 rounded-xl p-3 text-blue-100 text-xs italic">« {selectedLead.original_message} »</div>
              </div>
            )}

            <div className="flex gap-2 flex-wrap pt-2">
              {selectedLead.status === 'pending' && (
                <button onClick={() => updateStatus(selectedLead.id, 'confirmed')}
                  className="px-4 py-2 rounded-xl bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 text-xs font-medium">
                  ✓ Confirmer la vente
                </button>
              )}
              {(selectedLead.status === 'pending' || selectedLead.status === 'confirmed') && (
                <>
                  <button onClick={() => updateStatus(selectedLead.id, 'completed')}
                    className="px-4 py-2 rounded-xl bg-green-500/20 text-green-300 hover:bg-green-500/30 text-xs font-medium">
                    📦 Marquer livrée
                  </button>
                  <button onClick={() => updateStatus(selectedLead.id, 'cancelled')}
                    className="px-4 py-2 rounded-xl bg-red-500/20 text-red-300 hover:bg-red-500/30 text-xs font-medium">
                    ✕ Annuler
                  </button>
                </>
              )}
              {onOpenConversation && (
                <button
                  onClick={() => { setSelectedLead(null); onOpenConversation(); }}
                  className="px-4 py-2 rounded-xl bg-white/10 text-white hover:bg-white/20 text-xs font-medium flex items-center gap-1"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> Voir les conversations
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// 💬 PANNEAU CONVERSATIONS (inbox + prise de main)
// ════════════════════════════════════════════════════════════
export function ConversationsPanel({ clientId }) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const loadConversations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bot/conversations/${clientId}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setConversations(data.conversations);
    } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  const openConversation = async (conv) => {
    setActiveConv(conv);
    setMsgLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bot/conversations/${clientId}/${conv.id}/messages`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setMessages(data.messages);
    } finally {
      setMsgLoading(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  const toggleBot = async (conv) => {
    const res = await fetch(`${API_URL}/api/bot/conversations/${clientId}/${conv.id}/toggle-bot`, {
      method: 'POST', headers: authHeaders()
    });
    const data = await res.json();
    if (data.success) {
      setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, bot_paused: data.bot_paused } : c));
      if (activeConv?.id === conv.id) setActiveConv({ ...activeConv, bot_paused: data.bot_paused });
    }
  };

  const sendReply = async () => {
    if (!draft.trim() || !activeConv) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/bot/conversations/${clientId}/${activeConv.id}/send`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ message: draft })
      });
      const data = await res.json();
      if (data.success) {
        setMessages(prev => [...prev, { id: Date.now(), sender_type: 'human', text: draft, created_at: new Date().toISOString() }]);
        setDraft('');
        setActiveConv({ ...activeConv, bot_paused: true });
        setConversations(prev => prev.map(c => c.id === activeConv.id ? { ...c, bot_paused: true } : c));
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      } else {
        alert(data.message);
      }
    } finally { setSending(false); }
  };

  if (loading) return <PanelLoader />;

  // Vue thread
  if (activeConv) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <button onClick={() => { setActiveConv(null); loadConversations(); }}
            className="text-blue-300 text-sm hover:text-blue-200">← Toutes les conversations</button>
          <div className="flex items-center gap-2">
            <span className="text-white font-medium text-sm">{activeConv.sender_name}</span>
            <button
              onClick={() => toggleBot(activeConv)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all ${
                activeConv.bot_paused
                  ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                  : 'bg-green-500/20 text-green-300 border border-green-500/30'
              }`}
            >
              {activeConv.bot_paused ? <><Hand className="w-3.5 h-3.5" /> Vous avez la main — Rendre au bot</>
                                     : <><Bot className="w-3.5 h-3.5" /> Bot actif — Prendre la main</>}
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="bg-black/30 rounded-2xl p-3 max-h-80 overflow-y-auto space-y-2">
          {msgLoading ? <PanelLoader /> : messages.map(m => (
            <div key={m.id} className={`flex ${m.sender_type === 'user' ? 'justify-start' : 'justify-end'}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                m.sender_type === 'user' ? 'bg-white/10 text-white'
                : m.sender_type === 'human' ? 'bg-orange-600/40 text-orange-100'
                : 'bg-blue-600/40 text-blue-100'
              }`}>
                <div className="flex items-center gap-1 text-xs opacity-60 mb-0.5">
                  {m.sender_type === 'user' ? <><User className="w-3 h-3" /> Client</>
                   : m.sender_type === 'human' ? <><Hand className="w-3 h-3" /> Vous</>
                   : <><Bot className="w-3 h-3" /> Agent IA</>}
                </div>
                {m.text}
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        {/* Zone de réponse manuelle */}
        <div className="flex gap-2">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendReply()}
            placeholder="Répondre manuellement au client..."
            className="flex-1 bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-500"
          />
          <button
            onClick={sendReply}
            disabled={sending || !draft.trim()}
            className="px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        <p className="text-gray-500 text-xs">
          💡 Répondre manuellement met automatiquement le bot en pause sur cette conversation (les autres clients continuent d'avoir le bot).
        </p>
      </div>
    );
  }

  // Vue liste
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-gray-400 text-xs">
          {conversations.length} conversation{conversations.length > 1 ? 's' : ''} — cliquez pour voir les messages et reprendre la main si besoin.
        </p>
        <button onClick={loadConversations} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
      {conversations.length === 0 ? (
        <div className="p-6 text-center text-gray-400 text-sm">Aucune conversation pour le moment.</div>
      ) : conversations.map(conv => (
        <button
          key={conv.id}
          onClick={() => openConversation(conv)}
          className="w-full text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-3 transition-all"
        >
          <div className="flex items-center gap-2">
            <span className="text-white font-medium text-sm">{conv.sender_name}</span>
            {conv.bot_paused ? (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-orange-500/20 text-orange-300">
                <Hand className="w-3 h-3" /> Main humaine
              </span>
            ) : (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-300">
                <Bot className="w-3 h-3" /> Bot actif
              </span>
            )}
            <span className="text-gray-500 text-xs ml-auto">
              {conv.updated_at ? new Date(conv.updated_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
            </span>
          </div>
          <p className="text-gray-400 text-xs mt-1 truncate">
            {conv.last_sender === 'user' ? '👤 ' : conv.last_sender === 'human' ? '✋ ' : '🤖 '}{conv.last_message}
          </p>
        </button>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// 🔔 PANNEAU TELEGRAM (alertes en 1 clic)
// ════════════════════════════════════════════════════════════
export function TelegramPanel({ clientId }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bot/telegram/status/${clientId}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setStatus(data);
    } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const disconnect = async () => {
    if (!window.confirm('Désactiver les alertes Telegram pour cette page ?')) return;
    await fetch(`${API_URL}/api/bot/telegram/${clientId}/disconnect`, { method: 'POST', headers: authHeaders() });
    loadStatus();
  };

  if (loading) return <PanelLoader />;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-200 text-xs">
        <Bell className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>
          Recevez une alerte Telegram instantanée quand votre agent IA détecte une <strong>vente</strong>, une <strong>demande
          de RDV</strong> ou un client qui a besoin d'un <strong>humain</strong> — avec le lien direct vers la conversation.
        </span>
      </div>

      {status?.connected ? (
        <div className="flex items-center justify-between gap-3 p-4 rounded-2xl bg-green-500/10 border border-green-500/30">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-400" />
            <div>
              <div className="text-green-300 font-medium text-sm">Alertes Telegram activées</div>
              <div className="text-gray-400 text-xs">Vous recevez les alertes de cette page sur Telegram.</div>
            </div>
          </div>
          <button onClick={disconnect} className="px-3 py-2 rounded-xl bg-red-500/10 text-red-300 hover:bg-red-500/20 text-xs">
            Désactiver
          </button>
        </div>
      ) : (
        <div className="text-center py-4">
          <a
            href={status?.link_url || '#'}
            target="_blank"
            rel="noreferrer"
            onClick={() => setTimeout(loadStatus, 8000)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white font-bold text-sm transition-all hover:scale-105"
          >
            <Send className="w-4 h-4" /> Connecter mon Telegram <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <p className="text-gray-500 text-xs mt-3 max-w-sm mx-auto">
            1 clic : Telegram s'ouvre → appuyez sur <strong>« Démarrer / Start »</strong> → vos alertes sont activées automatiquement.
            Vous pouvez ensuite ajouter le bot dans un groupe Telegram de votre équipe si vous préférez.
          </p>
          <button onClick={loadStatus} className="mt-3 text-blue-300 text-xs hover:text-blue-200 flex items-center gap-1 mx-auto">
            <RefreshCw className="w-3 h-3" /> J'ai cliqué sur Start — vérifier
          </button>
        </div>
      )}
    </div>
  );
}

// ════════ Helpers UI ════════
const PanelLoader = () => (
  <div className="py-8 text-center"><Loader2 className="w-7 h-7 text-blue-400 animate-spin mx-auto" /></div>
);

const MiniStat = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl p-3">
    <Icon className={`w-4 h-4 ${color} mb-1`} />
    <div className="text-white font-bold text-lg">{value}</div>
    <div className="text-gray-500 text-xs">{label}</div>
  </div>
);

const DetailRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 border-b border-white/5 pb-2">
    <span className="text-gray-400 text-xs">{label}</span>
    <span className="text-white text-sm">{value}</span>
  </div>
);

const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
    <div
      className="bg-slate-900 border border-white/10 rounded-3xl p-6 w-full max-w-md max-h-[85vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-bold">{title}</h3>
        <button onClick={onClose} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white">
          <X className="w-4 h-4" />
        </button>
      </div>
      {children}
    </div>
  </div>
);
